import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { getStatusColor, Status, User } from 'components';
import { Empty } from 'components/Empty';
import { theme, toRem } from 'design';
import { URL } from 'routes';
import { DisbursementMethod, IEmployeePayrollTip, IEmployeeSite, IPayPeriod, PAYOUT_STATUS_DISPLAY_ORDER } from 'types';
import { capitalize, centsToDollarsDisplay, centsToNegativeDollarsDisplay, isSortDesc, isSortedBy } from 'utils';
import { Cell } from '../Cell';
import HeaderCell from '../HeaderCell';
import HeaderRow from '../HeaderRow';
import Row from '../Row';
import SortLabel from '../SortLabel';
import Table from '../Table';
import TableBody from '../TableBody';
import TableHead from '../TableHead';

const RowStatusWrapper = styled.div`
  display: grid;
  grid-template-columns: auto;
  grid-gap: ${toRem(theme.space.xs)};
  width: 100%;

  > * {
    margin-bottom: 0;
    color: ${theme.color.neutral[900]};
  }
`;

const sumByMethod = ({ instant, payroll, cash }: IEmployeePayrollTip, field: 'tips' | 'autoGratuities') => (instant?.[field] ?? 0) + (payroll?.[field] ?? 0) + (cash?.[field] ?? 0);

// The amount columns after Name and Status: header, sort key when sortable, cell hook and value.
const AMOUNT_COLUMNS: { label: string; sort?: string; dataCy: string; value: (tip: IEmployeePayrollTip) => React.ReactNode }[] = [
  { label: 'Tips', dataCy: 'tips', value: (tip) => centsToDollarsDisplay(sumByMethod(tip, 'tips')) },
  { label: 'Autograts', dataCy: 'autograts', value: (tip) => centsToDollarsDisplay(sumByMethod(tip, 'autoGratuities')) },
  { label: 'Gross', sort: 'gross', dataCy: 'gross', value: (tip) => centsToDollarsDisplay(tip.gross ?? 0) },
  { label: 'Holdback', sort: 'holdback', dataCy: 'holdback', value: (tip) => centsToNegativeDollarsDisplay(tip.holdback ?? 0) },
  { label: 'Instant', sort: DisbursementMethod.INSTANT, dataCy: 'instant', value: (tip) => centsToDollarsDisplay((tip.instant?.total ?? 0) - (tip.holdback ?? 0)) },
  { label: 'Payroll', sort: DisbursementMethod.PAYROLL, dataCy: 'payroll', value: (tip) => centsToDollarsDisplay(tip.payroll?.total ?? 0) },
  { label: 'Cash', sort: DisbursementMethod.CASH, dataCy: 'cash', value: (tip) => centsToDollarsDisplay(tip.cash?.total ?? 0) },
  { label: 'Net', sort: 'net', dataCy: 'total', value: (tip) => <strong>{centsToDollarsDisplay(tip.net ?? 0)}</strong> },
];

interface IPayrollTipsRowProps {
  tipData?: IEmployeePayrollTip;
  isLoading?: boolean;
  employeeId?: string;
  onClick?: () => void;
}

export const PayrollTipsRow = ({ tipData, employeeId, isLoading, onClick, ...rest }: IPayrollTipsRowProps) => {
  const { employee, statusCounts } = tipData || {};

  return (
    <Row clickable={!isLoading && !!onClick} onClick={onClick} {...rest}>
      <Cell data-cy="payee-name">
        <User hasPaymentMethod={!!employee?.user?.card} id={employee?.user?.id} image={employee?.user?.avatarUrl} isLoading={isLoading} username={employee?.name} details={employeeId} />
      </Cell>

      <Cell data-cy="status">
        {isLoading ? (
          <Skeleton width={55} />
        ) : (
          <RowStatusWrapper>
            {PAYOUT_STATUS_DISPLAY_ORDER.filter((status) => statusCounts?.[status] > 0).map((status) => (
              <Status key={status} data-cy={`status-${status}`} appearance={getStatusColor(status)} label={`${capitalize(status)} (${statusCounts[status]})`} />
            ))}
          </RowStatusWrapper>
        )}
      </Cell>

      {AMOUNT_COLUMNS.map(({ dataCy, value }) => (
        <Cell key={dataCy} align="left" minWidth={88} data-cy={dataCy}>
          {isLoading ? <Skeleton width={88} /> : value(tipData)}
        </Cell>
      ))}
    </Row>
  );
};

interface IPayrollTipsTableProps {
  isLoading?: boolean;
  tips: IEmployeePayrollTip[];
  currentSiteId: string;
  currentSort: string;
  onSortChange: (sort: string) => void;
  selectedPayPeriod?: IPayPeriod;
}

const PayrollTipsTable: React.FC<IPayrollTipsTableProps> = ({ isLoading, tips, currentSiteId, currentSort, onSortChange, selectedPayPeriod }) => {
  const history = useHistory();
  const isDescendingSort = isSortDesc(currentSort);

  if (!isLoading && !selectedPayPeriod) return null;

  return (
    <Table sticky scrollable data-cy="reports-payroll-tips-table">
      <TableHead>
        <HeaderRow sticky>
          <HeaderCell>
            <SortLabel label="Name" data-cy="employee-name-header" isSorted={isSortedBy('employee_name', currentSort)} isSortedDesc={isDescendingSort} onClick={() => onSortChange('employee_name')} isLoading={isLoading} />
          </HeaderCell>
          <HeaderCell>{!isLoading && 'Status'}</HeaderCell>
          {AMOUNT_COLUMNS.map(({ label, sort }) => (
            <HeaderCell key={label} align="left">
              {sort ? <SortLabel label={label} data-cy={`${sort}-header`} isSorted={isSortedBy(sort, currentSort)} isSortedDesc={isDescendingSort} onClick={() => onSortChange(sort)} isLoading={isLoading} /> : !isLoading && label}
            </HeaderCell>
          ))}
        </HeaderRow>
      </TableHead>
      <TableBody>
        {isLoading ? (
          [...Array(10)].map((_, i) => <PayrollTipsRow key={`payroll-tips-row-loading-${i}`} isLoading data-cy="loading-row" />)
        ) : tips.length === 0 ? (
          <Row>
            <Cell colSpan={10}>
              <Empty container containerHeight={300} icon="Payment" message="No results for selected payroll period." />
            </Cell>
          </Row>
        ) : (
          tips.map((datum: IEmployeePayrollTip) => {
            const siteEmployeeId = (datum.employee.sites || []).find((site: IEmployeeSite) => site.id === currentSiteId)?.employeeId || '--';

            return (
              <PayrollTipsRow
                key={`${datum.employee.id}-${datum.paymentId}`}
                data-cy="payroll-tips-row"
                data-value={datum.employee.id}
                employeeId={siteEmployeeId}
                tipData={datum}
                onClick={() => {
                  history.push({
                    pathname: `${URL.ReportsEmployeePayPeriod}/${datum.employee.id}/${selectedPayPeriod.start}/${selectedPayPeriod.end}`,
                  });
                }}
              />
            );
          })
        )}
      </TableBody>
    </Table>
  );
};

export default PayrollTipsTable;
